import { type LucideIcon } from "lucide-react";
import { Badge } from "../data-display/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../primitives/tooltip";
import { cn } from "../utils";
import { statusBadgeVariants, type StatusVariant } from "./status-badge-variants";

export type { StatusVariant };

export interface StatusBadgeProps {
  variant: StatusVariant;
  children: React.ReactNode;
  icon?: LucideIcon;
  className?: string;
  /** Extra classes for the icon (e.g. `animate-spin` for in-progress states). */
  iconClassName?: string;
}

export function StatusBadge({
  variant,
  children,
  icon: Icon,
  className,
  iconClassName,
}: StatusBadgeProps) {
  return (
    <Badge className={cn(statusBadgeVariants({ variant }), className)}>
      {Icon && <Icon className={cn("h-3 w-3", iconClassName)} />}
      {children}
    </Badge>
  );
}

export interface StatusBadgeTooltipProps {
  /** The badge to wrap. Rendered as-is when `reason` is absent. */
  badge: React.ReactNode;
  /**
   * Why the badge is in its current state, in words the viewer can act on. A
   * bare status badge says something is wrong (or noteworthy) without saying
   * what — this carries the "what" on hover/focus instead of forcing a trip
   * into the surface it summarizes. `tabIndex` keeps it reachable without a
   * pointer. Pass `null`/`undefined` to render the badge plain.
   */
  reason?: React.ReactNode | null;
  side?: "top" | "right" | "bottom" | "left";
  /** Extra classes on the focusable wrapper (e.g. `ml-auto` in a flex row). */
  className?: string;
}

export function StatusBadgeTooltip({
  badge,
  reason,
  side = "top",
  className,
}: StatusBadgeTooltipProps) {
  if (!reason) return <>{badge}</>;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            className={cn(
              "shrink-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
              className,
            )}
          >
            {badge}
          </span>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-xs">
          {reason}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
